import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';

const InfoIcon = ({ className }: { className?: string }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className}
    >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);

type PopupPosition = 'top' | 'bottom' | 'left' | 'right';

interface FeatureUnavailablePopupProps {
  children: React.ReactNode;
  className?: string;
  message?: string;
  position?: PopupPosition;
}

const FeatureUnavailablePopup: React.FC<FeatureUnavailablePopupProps> = ({ 
  children, 
  className = '', 
  message,
  position = 'top' 
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const triggerPopup = useCallback(() => {
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 2000);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    triggerPopup();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      triggerPopup();
    }
  };

  const getPositionClasses = (): string => {
    switch (position) {
      case 'bottom':
        return 'top-full left-1/2 -translate-x-1/2 mt-2';
      case 'left':
        return 'top-1/2 right-full -translate-y-1/2 mr-2';
      case 'right':
        return 'top-1/2 left-full -translate-y-1/2 ml-2';
      case 'top':
      default:
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
    }
  };

  useLayoutEffect(() => {
    if (showPopup && popupRef.current) {
      const popup = popupRef.current;
      popup.style.visibility = 'hidden'; // Hide until position is calculated

      // Apply base positioning to measure
      popup.className = `
        absolute flex items-center gap-2 bg-gray-800 text-white text-center
        px-4 py-2 rounded-md shadow-lg transition-opacity duration-300
        pointer-events-none w-max max-w-xs z-50
        ${getPositionClasses()}
        ${showPopup ? 'opacity-100' : 'opacity-0'}
      `;

      const rect = popup.getBoundingClientRect();

      // Check viewport collision
      if (rect.left < 0) {
        popup.style.left = '0';
        popup.style.right = 'auto';
        popup.style.transform = 'translateX(0)';
      }
      if (rect.right > window.innerWidth) {
        popup.style.left = 'auto';
        popup.style.right = '0';
        popup.style.transform = 'translateX(0)';
      }
      if (rect.top < 0) {
        popup.style.top = '0';
        popup.style.bottom = 'auto';
        popup.style.transform = 'translateY(0)';
      }
      if (rect.bottom > window.innerHeight) {
        popup.style.top = 'auto';
        popup.style.bottom = '0';
        popup.style.transform = 'translateY(0)';
      }
      
      popup.style.visibility = 'visible'; // Make it visible after positioning
    }
  }, [showPopup, position]);

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      {children}
      <div
        className="absolute inset-0 z-10 cursor-pointer"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={message || "This feature is not available in the beta version."}
      ></div>
      <div
        ref={popupRef}
        className={`
          absolute transition-opacity duration-300 pointer-events-none
          opacity-0 ${showPopup ? 'opacity-100' : ''}
        `}
        role="alert"
        aria-live="polite"
      >
        <div className="flex items-center gap-2 bg-gray-800 text-white text-center px-4 py-2 rounded-md shadow-lg">
            <InfoIcon className="w-5 h-5 text-blue-400" />
            <span>{message ? message : "Feature not available in beta."}</span>
        </div>
      </div>
    </div>
  );
};

export default FeatureUnavailablePopup;